/**
 * Subdomain → tenant resolver.
 *
 * On apex (physiogenic.app) renders default branding.
 * On a tenant subdomain (acme.physiogenic.app) the API reads the `Host` header
 * and returns the clinic_id via `x-clinic-id` response header. This module:
 *   1. extracts the subdomain from window.location.hostname,
 *   2. fetches /api/public/clinics/<slug> for branding overrides,
 *   3. exposes the data via React context.
 *
 * Apps can then read `useSubdomainTenant()` to swap logo / name / primary color.
 */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { api } from './api';

const RESERVED = new Set(['app', 'www', 'api', 'admin', 'staff', 'status', 'blog', 'preview', 'localhost']);

// In-memory cache so we only fetch each slug once per page load (and remember
// 404s so we don't hammer the API on every component remount).
const _slugCache = new Map();

function extractSubdomain() {
  const host = window.location.hostname;
  const parts = host.split('.');
  if (parts.length < 3) return null;
  const first = parts[0];
  if (RESERVED.has(first)) return null;
  if (first.replace(/-/g, '').match(/^\d+$/)) return null;
  // Skip the Emergent preview-host pattern (e.g. "rehab-compliance-eu" on
  // `*.preview.emergentagent.com`) — these never map to a real tenant.
  if (host.endsWith('.preview.emergentagent.com')) return null;
  return first;
}

const Ctx = createContext({ tenant: null, slug: null, loading: false });

export function SubdomainTenantProvider({ children }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const slug = extractSubdomain();

  useEffect(() => {
    if (!slug) { setLoading(false); return; }
    let mounted = true;
    if (_slugCache.has(slug)) {
      const cached = _slugCache.get(slug);
      setTenant(cached);
      setLoading(false);
      return;
    }
    api.get(`/public/clinics/${slug}`)
      .then(r => {
        _slugCache.set(slug, r.data);
        if (mounted) setTenant(r.data);
      })
      .catch(() => {
        _slugCache.set(slug, null); // remember the miss
        if (mounted) setTenant(null);
      })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [slug]);

  // Inject CSS variable for the tenant's brand color when present.
  useEffect(() => {
    if (tenant?.custom_brand_color) {
      document.documentElement.style.setProperty('--tenant-brand', tenant.custom_brand_color);
    }
  }, [tenant]);

  return (
    <Ctx.Provider value={{ tenant, slug, loading }}>
      {children}
    </Ctx.Provider>
  );
}

export function useSubdomainTenant() {
  return useContext(Ctx);
}
